import {Tooltip} from "primereact/tooltip";
import {useRef} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import {selectByLanguage} from "@reva/shared/i18n";
import {SEGMENT} from "@reva/shared/reva-spec";

import {useRevaTranslation} from "../hooks/languageHooks";
import {navigateToRouteAndHash} from "../hooks/navigationHooks";
import {
    selectSegmentBuildingTypeRentedIndicationPayload,
    selectValueAndLendingValueCurried,
} from "../redux/indicationSelectors";
import {selectValueAndLendingValueSpecCurried} from "../redux/specSelectors";
import {selectSuggestedParameters} from "../redux/valueIndicationSelectors";
import {selectLoading} from "../redux/valueIndicationSlice";
import {formatValueInfo} from "../util/numberFormat";
import {snakeToCamel} from "../util/stringUtil";

const MARKET_ADJUSTMENT_FACTOR = "market_adjustment_factor";
/** Nennung des Marktanpassungsfaktors im indication_hint (de/en) */
const MARKET_ADJUSTMENT_FACTOR_TERM = /Marktanpassungsfaktors?|market adjustment factor/i;

/**
 * Hinweistext, in dem die erste Nennung des Marktanpassungsfaktors zur Eingabe im Objekttab springt.
 * Spätere Nennungen („alternativen Marktanpassungsfaktor (0,3)“) meinen den Vorschlagswert und bleiben Text.
 * @param {object} props
 * @param {string} props.hint Hinweistext der AVM
 * @param {() => void} props.onJump Sprung zur Eingabe
 * @return {JSX.Element}
 */
function HintWithMarketAdjustmentFactorLink({hint, onJump}) {
    const match = MARKET_ADJUSTMENT_FACTOR_TERM.exec(hint);
    if (!match) return <>{hint}</>;
    const end = match.index + match[0].length;
    return (
        <>
            {hint.slice(0, match.index)}
            <a
                href={`#${snakeToCamel(`input_${MARKET_ADJUSTMENT_FACTOR}`)}`}
                className="market-adjustment-factor-link underline cursor-pointer"
                style={{color: "inherit"}}
                onClick={(event) => {
                    event.preventDefault();
                    onJump();
                }}
            >
                {match[0]}
            </a>
            {hint.slice(end)}
        </>
    );
}

/**
 * Component to render the value and lending value information.
 * @param {object} [props]
 * @param {string} [props.className] additional class name for styling
 * @param {RevaIndicationEndpoint|null} [props.endpoint] endpoint to fetch data from
 * @return {JSX.Element} rendered component
 */
function ValueLendingValueInfo({className, endpoint} = {}) {
    const {t, language} = useRevaTranslation();
    const ref = useRef(null);
    const payload = useSelector(selectSegmentBuildingTypeRentedIndicationPayload);
    const isRentedApartment = payload?.segment === SEGMENT.WHG_M;
    const valueAndLendingValue = useSelector(selectValueAndLendingValueCurried(endpoint));
    const valueSpec = useSelector(selectValueAndLendingValueSpecCurried(endpoint ?? "VALUE", isRentedApartment));
    const indicationLoading = useSelector(selectLoading);
    const suggestedParameters = useSelector(selectSuggestedParameters);
    const navigate = useNavigate();
    const formatedValues = formatValueInfo(valueAndLendingValue, valueSpec, language);

    const indicationName =
        endpoint === undefined || endpoint === "VALUE"
            ? selectByLanguage(language, valueAndLendingValue?.indicationNameDe, valueAndLendingValue?.indicationNameEn)
            : undefined;

    const indicationHint = selectByLanguage(
        language,
        valueAndLendingValue?.indication_hint_de,
        valueAndLendingValue?.indication_hint,
    );

    // Nur mit Vorschlag der AVM: dann ist die Eingabe im Objekttab zwangsweise aufgeklappt
    const linkMarketAdjustmentFactor = !!indicationHint && suggestedParameters.includes(MARKET_ADJUSTMENT_FACTOR);
    const jumpToMarketAdjustmentFactor = () =>
        navigateToRouteAndHash(
            navigate,
            `/object/overview${window.location.search}`,
            snakeToCamel(`input_${MARKET_ADJUSTMENT_FACTOR}`),
            {focus: true},
        );

    const values = [
        {
            title: isRentedApartment ? t("value.rentingValue") : t("value.value"),
            amount: formatedValues.value,
            perSqM: formatedValues.valueSqm,
            hasValue: valueAndLendingValue?.value != null || valueAndLendingValue?.value_per_sqm != null,
        },
        {
            title: t("value.lendingValue"),
            amount: formatedValues.valueLending,
            perSqM: formatedValues.valueLendingSqm,
            hasValue:
                valueAndLendingValue?.lending_value != null || valueAndLendingValue?.lending_value_per_sqm != null,
        },
    ];

    return (
        <div className={`flex flex-row gap-8 rounded-border bg-surface-100 dark:bg-surface-700 p-4 mb-6 ${className}`}>
            <div>
                {values.map((item, index) => (
                    <RenderValue key={item.title} index={index}>
                        <span className="font-bold">{item.title}</span>
                    </RenderValue>
                ))}
            </div>

            {indicationLoading && (
                <div>
                    {values.map((item, index) => (
                        <RenderValue key={`loading${index}`} index={index}>
                            <i className="pi pi-spin pi-spinner" />
                        </RenderValue>
                    ))}
                </div>
            )}

            {!indicationLoading && (
                <>
                    <div className="shrink-0">
                        {values.map((item, index) => (
                            <RenderValue index={index} key={`${item.title}-${item.amount}-${index}`}>
                                <span>{item.hasValue ? item.amount : "-"}</span>
                            </RenderValue>
                        ))}
                    </div>
                    <div className="shrink-0">
                        {values.map((item, index) => (
                            <RenderValue key={`${item.title}-perSqM-${index}`} index={index}>
                                <span>{item?.perSqM}</span>
                            </RenderValue>
                        ))}
                    </div>
                    {indicationName && (
                        <div className="shrink-0">
                            {values.map((item, index) => (
                                <RenderValue key={`${item.title}-${indicationName}-${index}`} index={index}>
                                    <span>
                                        {item.hasValue
                                            ? t("value.derivedValueFrom", {derivedFrom: indicationName})
                                            : " "}
                                    </span>
                                </RenderValue>
                            ))}
                        </div>
                    )}
                    {valueAndLendingValue?.showWarning && (
                        <span ref={ref} className="font-bold p-error">
                            {linkMarketAdjustmentFactor ? (
                                <HintWithMarketAdjustmentFactorLink
                                    hint={indicationHint}
                                    onJump={jumpToMarketAdjustmentFactor}
                                />
                            ) : (
                                (indicationHint ?? t("value.badConvergence"))
                            )}
                        </span>
                    )}
                    <Tooltip
                        target={ref}
                        className="custom-tooltip"
                        position="top"
                        content={t("value.badConvergenceTooltip")}
                    />
                </>
            )}
        </div>
    );
}

const RenderValue = ({children, index}) => <div className={index === 0 ? "pb-6" : ""}>{children}</div>;

export default ValueLendingValueInfo;
